import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanOutpaintParams {
  /**
   * 任务ID
   */
  jobId: string;
  /**
   * 图像编号，必须是0、1、2或3
   * @values 0 | 1 | 2 | 3
   */
  imageNo: 0 | 1 | 2 | 3;
  /**
   * 扩展比例，必须是1.1到2.0之间的数字
   */
  scale: number;
  /**
   * 重混提示词，长度必须在1-8192字符之间
   */
  remixPrompt?: string;
  /**
   * 回调URL，必须是有效的HTTP/HTTPS URL
   */
  callback?: string;
}

export interface YouchuanOutpaintResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_outpaint
 *
 * Youchuan Outpaint Image Generator
 *
 * Image outpainting (extension) implementation for Youchuan Open Platform (悠船开放平台).
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/api_outpaint
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanOutpaint } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanOutpaint({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   scale: 1.5
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanOutpaintParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanOutpaint(
  params: YouchuanOutpaintParams,
): Promise<YouchuanOutpaintResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_outpaint',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanOutpaintResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
